import type { Funnel } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Filter, Plus } from "lucide-react";
import { useMemo } from "react";
import { PointsDelta } from "@/components/domain/analytics";
import { PageHeader, TimeAgo } from "@/components/domain/misc";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { type ColumnDef, DataTable } from "@/components/ui/data-table";
import { Meter } from "@/components/ui/misc";
import { FunnelStatusBadge, STEP_KIND_META, WINDOW_LABEL } from "@/features/funnels/meta";
import { SuggestedFunnels } from "@/features/funnels/suggestions";
import { q } from "@/lib/api";
import { fmtInt, fmtPct } from "@/lib/format";

export const Route = createFileRoute("/_app/funnels/")({
  component: FunnelsPage,
});

function FunnelsPage() {
  const navigate = Route.useNavigate();
  const funnels = useQuery({ ...q.funnels(), refetchInterval: (query) => (query.state.data?.some((f) => f.status === "backfilling") ? 5_000 : false) });
  const agents = useQuery(q.agents());
  const names = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name])), [agents.data]);

  const columns = useMemo<ColumnDef<Funnel, unknown>[]>(
    () => [
      {
        id: "name",
        header: "Funnel",
        accessorKey: "name",
        cell: ({ row }) => (
          <span className="block min-w-[200px] max-w-[300px]">
            <span className="block truncate text-[13px] font-medium text-fg">{row.original.name}</span>
            <span className="block truncate text-xs text-fg-3" title={row.original.description}>
              {row.original.description || WINDOW_LABEL[row.original.window]}
            </span>
          </span>
        ),
      },
      {
        id: "agents",
        header: "Agents",
        accessorFn: (f) => f.agentIds.map((id) => names.get(id) ?? id).join(", "),
        cell: ({ getValue }) => <span className="block max-w-[180px] truncate font-mono text-[12px] text-fg-2">{String(getValue())}</span>,
      },
      {
        id: "steps",
        header: "Steps",
        accessorFn: (f) => f.steps.length,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5">
            <span className="tabular text-fg">{row.original.steps.length}</span>
            <span className="flex items-center gap-0.5 text-fg-3 [&_svg]:size-3.5" aria-label={row.original.steps.map((s) => STEP_KIND_META[s.kind].label).join(", ")}>
              {row.original.steps.map((s) => (
                <span key={s.id} title={`${s.name} · ${STEP_KIND_META[s.kind].label}`}>
                  {STEP_KIND_META[s.kind].icon}
                </span>
              ))}
            </span>
          </span>
        ),
      },
      {
        id: "entered",
        header: "Entered 7d",
        accessorKey: "entered7d",
        cell: ({ row }) => <span className="font-medium text-fg">{fmtInt(row.original.entered7d)}</span>,
        meta: { align: "right" },
        sortDescFirst: true,
      },
      {
        id: "conversion",
        header: "Conversion",
        accessorKey: "conversion7d",
        cell: ({ row }) => (
          <span className="inline-flex items-center justify-end gap-2">
            <span className="font-medium text-fg">{fmtPct(row.original.conversion7d, 1)}</span>
            <PointsDelta value={row.original.conversionDelta} className="w-14 text-right" />
          </span>
        ),
        meta: { align: "right" },
        sortDescFirst: true,
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) =>
          row.original.status === "backfilling" ? (
            <span className="flex w-36 flex-col gap-1">
              <FunnelStatusBadge funnel={row.original} />
              <Meter value={row.original.backfillProgress} label={`Backfill ${fmtPct(row.original.backfillProgress)}`} tone="var(--violet)" />
            </span>
          ) : (
            <FunnelStatusBadge funnel={row.original} />
          ),
      },
      {
        id: "created",
        header: "Created",
        accessorKey: "createdAt",
        cell: ({ row }) => (
          <span className="text-xs text-fg-3">
            <TimeAgo iso={row.original.createdAt} className="text-xs" /> · {row.original.owner.split("@")[0]}
          </span>
        ),
        meta: { responsive: "hidden 2xl:table-cell" },
      },
    ],
    [names],
  );

  return (
    <Page>
      <PageHeader
        title="Funnels"
        description="What progress looks like for your agents: 2–12 steps matched by events, tool calls, outcomes, use cases or plain-language descriptions. New funnels backfill over stored sessions, so they have data in minutes."
        actions={
          <Button size="sm" asChild>
            <Link to="/funnels/new">
              <Plus /> New funnel
            </Link>
          </Button>
        }
      />
      <SuggestedFunnels agentNames={names} />
      <Card>
        {funnels.error ? (
          <ErrorPanel error={funnels.error} reset={() => void funnels.refetch()} className="m-4" />
        ) : (
          <DataTable<Funnel>
            data={funnels.data}
            loading={funnels.isPending}
            columns={columns}
            getRowId={(f) => f.id}
            stickyHeader={false}
            onRowClick={(f) => void navigate({ to: "/funnels/$funnelId", params: { funnelId: f.id } })}
            empty={{
              icon: <Filter />,
              title: "No funnels yet",
              description: (
                <>
                  Suggestions built from your conversations appear above after the nightly analysis; or define the steps that matter yourself, e.g. return request → exchange
                  offered → exchange accepted.{" "}
                  <Link to="/funnels/new" className="text-accent hover:underline">
                    Create a funnel
                  </Link>
                </>
              ),
            }}
          />
        )}
      </Card>
    </Page>
  );
}
